
import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Incident {
  id: string;
  correlativo: string;
  operacion: string;
  cliente: string;
  tipo: string;
  descripcion: string;
  gravedad: 'baja' | 'media' | 'alta' | 'critica';
  responsable: string;
  estado: 'abierta' | 'en_proceso' | 'resuelta';
  fechaCreacion: string;
  fechaResolucion?: string;
  evidencia?: string;
}

const incidentsData: Incident[] = [
  {
    id: '1',
    correlativo: 'INC-2024-0089',
    operacion: 'OP-2024-1823',
    cliente: 'Importadora Global S.A.',
    tipo: 'Documentación',
    descripcion: 'Certificado de origen con datos incorrectos del proveedor',
    gravedad: 'alta',
    responsable: 'María González',
    estado: 'abierta',
    fechaCreacion: '2024-01-15',
    evidencia: 'certificado_origen_error.pdf'
  },
  {
    id: '2',
    correlativo: 'INC-2024-0088',
    operacion: 'OP-2024-1815',
    cliente: 'Exportadora del Sur Ltda.',
    tipo: 'Transporte',
    descripcion: 'Contenedor llegó con daños visibles en el sello',
    gravedad: 'critica',
    responsable: 'Carlos Ramírez',
    estado: 'en_proceso',
    fechaCreacion: '2024-01-14',
    evidencia: 'foto_contenedor.jpg'
  },
  {
    id: '3',
    correlativo: 'INC-2024-0087',
    operacion: 'OP-2024-1808',
    cliente: 'Comercial Andina S.A.C.',
    tipo: 'Aduana',
    descripcion: 'Retención por discrepancia en clasificación arancelaria',
    gravedad: 'alta',
    responsable: 'Ana Torres',
    estado: 'en_proceso',
    fechaCreacion: '2024-01-13'
  },
  {
    id: '4',
    correlativo: 'INC-2024-0086',
    operacion: 'OP-2024-1801',
    cliente: 'Distribuidora Pacífico E.I.R.L.',
    tipo: 'Cliente',
    descripcion: 'Cliente solicita cambio de dirección de entrega urgente',
    gravedad: 'media',
    responsable: 'Luis Mendoza',
    estado: 'resuelta',
    fechaCreacion: '2024-01-12',
    fechaResolucion: '2024-01-13'
  },
  {
    id: '5',
    correlativo: 'INC-2024-0085',
    operacion: 'OP-2024-1795',
    cliente: 'Logística Internacional Corp.',
    tipo: 'Documentación',
    descripcion: 'Factura comercial sin firma del exportador',
    gravedad: 'media',
    responsable: 'María González',
    estado: 'resuelta',
    fechaCreacion: '2024-01-11',
    fechaResolucion: '2024-01-12'
  },
  {
    id: '6',
    correlativo: 'INC-2024-0084',
    operacion: 'OP-2024-1788',
    cliente: 'Importadora Global S.A.',
    tipo: 'Almacenaje',
    descripcion: 'Mercancía requiere condiciones especiales de temperatura',
    gravedad: 'alta',
    responsable: 'Carlos Ramírez',
    estado: 'abierta',
    fechaCreacion: '2024-01-10'
  },
  {
    id: '7',
    correlativo: 'INC-2024-0083',
    operacion: 'OP-2024-1780',
    cliente: 'Textiles Modernos S.A.',
    tipo: 'Transporte',
    descripcion: 'Retraso en llegada del transportista al puerto',
    gravedad: 'baja',
    responsable: 'Ana Torres',
    estado: 'resuelta',
    fechaCreacion: '2024-01-09',
    fechaResolucion: '2024-01-10'
  },
  {
    id: '8',
    correlativo: 'INC-2024-0082',
    operacion: 'OP-2024-1775',
    cliente: 'Alimentos del Norte S.A.C.',
    tipo: 'Aduana',
    descripcion: 'Inspección física solicitada por autoridad aduanera',
    gravedad: 'media',
    responsable: 'Luis Mendoza',
    estado: 'en_proceso',
    fechaCreacion: '2024-01-08'
  }
];

export default function OperationsIncidents() {
  const [selectedFilter, setSelectedFilter] = useState('todas');
  const [selectedTipo, setSelectedTipo] = useState('todos');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const displayToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const getGravedadColor = (gravedad: string) => {
    switch (gravedad) {
      case 'critica': return 'bg-[#EF4444] text-white';
      case 'alta': return 'bg-[#F59E0B] text-white';
      case 'media': return 'bg-[#EAB308] text-white';
      case 'baja': return 'bg-[#10B981] text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'abierta': return 'bg-[#EF4444]/10 text-[#EF4444]';
      case 'en_proceso': return 'bg-[#2F80ED]/10 text-[#2F80ED]';
      case 'resuelta': return 'bg-[#10B981]/10 text-[#10B981]';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'abierta': return 'Abierta';
      case 'en_proceso': return 'En Proceso';
      case 'resuelta': return 'Resuelta';
      default: return estado;
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'Documentación': return 'ri-file-text-line';
      case 'Transporte': return 'ri-truck-line';
      case 'Aduana': return 'ri-building-line';
      case 'Cliente': return 'ri-user-line';
      case 'Almacenaje': return 'ri-archive-line';
      default: return 'ri-alert-line';
    }
  };

  const filteredIncidents = incidentsData.filter(inc => {
    if (selectedFilter !== 'todas' && inc.estado !== selectedFilter) return false;
    if (selectedTipo !== 'todos' && inc.tipo !== selectedTipo) return false;
    return true;
  });

  const stats = {
    total: incidentsData.length,
    abiertas: incidentsData.filter(i => i.estado === 'abierta').length,
    enProceso: incidentsData.filter(i => i.estado === 'en_proceso').length,
    resueltas: incidentsData.filter(i => i.estado === 'resuelta').length,
    criticas: incidentsData.filter(i => i.gravedad === 'critica').length
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Incidencias</h1>
            <p className="text-sm text-[#6B7280] mt-1">Registro y seguimiento de problemas operativos</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line mr-2"></i>Exportar
            </button>
            <button 
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>Nueva Incidencia
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Total</span>
              <div className="w-8 h-8 rounded-lg bg-[#0A2A43]/10 flex items-center justify-center">
                <i className="ri-file-list-3-line text-[#0A2A43]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#0A2A43]">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Abiertas</span>
              <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <i className="ri-error-warning-line text-[#EF4444]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#EF4444]">{stats.abiertas}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">En Proceso</span>
              <div className="w-8 h-8 rounded-lg bg-[#2F80ED]/10 flex items-center justify-center">
                <i className="ri-loader-4-line text-[#2F80ED]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#2F80ED]">{stats.enProceso}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Resueltas</span>
              <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <i className="ri-check-double-line text-[#10B981]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#10B981]">{stats.resueltas}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Críticas</span>
              <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-[#EF4444]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#EF4444]">{stats.criticas}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedFilter('todas')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedFilter === 'todas'
                    ? 'bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white'
                    : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                }`}
              >
                Todas ({stats.total})
              </button>
              <button
                onClick={() => setSelectedFilter('abierta')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedFilter === 'abierta'
                    ? 'bg-[#EF4444] text-white'
                    : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                }`}
              >
                Abiertas ({stats.abiertas})
              </button>
              <button
                onClick={() => setSelectedFilter('en_proceso')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedFilter === 'en_proceso'
                    ? 'bg-[#2F80ED] text-white'
                    : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                }`}
              >
                En Proceso ({stats.enProceso})
              </button>
              <button
                onClick={() => setSelectedFilter('resuelta')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedFilter === 'resuelta'
                    ? 'bg-[#10B981] text-white'
                    : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                }`}
              >
                Resueltas ({stats.resueltas})
              </button>
            </div>
            <div className="flex items-center gap-3">
              <select 
                value={selectedTipo}
                onChange={(e) => setSelectedTipo(e.target.value)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"
              >
                <option value="todos">Todos los tipos</option>
                <option value="Documentación">Documentación</option>
                <option value="Transporte">Transporte</option>
                <option value="Aduana">Aduana</option>
                <option value="Cliente">Cliente</option>
                <option value="Almacenaje">Almacenaje</option>
              </select>
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Incidencia</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Operación</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Gravedad</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Responsable</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#6B7280] uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredIncidents.map((inc) => (
                  <tr key={inc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-[#0A2A43]/10 flex items-center justify-center`}>
                          <i className={`${getTipoIcon(inc.tipo)} text-[#0A2A43] text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#0A2A43]">{inc.correlativo}</p>
                          <p className="text-xs text-[#6B7280]">{inc.fechaCreacion}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-[#2F80ED]">{inc.operacion}</p>
                      <p className="text-xs text-[#6B7280]">{inc.cliente}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#6B7280]">
                        <i className={getTipoIcon(inc.tipo)}></i>
                        {inc.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-4 max-w-xs">
                      <p className="text-sm text-[#6B7280] truncate">{inc.descripcion}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getGravedadColor(inc.gravedad)}`}>
                        {inc.gravedad.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#0A2A43]">{inc.responsable}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getEstadoColor(inc.estado)}`}>
                        {getEstadoLabel(inc.estado)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedIncident(inc);
                            setShowDetailModal(true);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer"
                          title="Ver detalle"
                        >
                          <i className="ri-eye-line text-[#6B7280]"></i>
                        </button>
                        <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer" title="Editar">
                          <i className="ri-edit-line text-[#6B7280]"></i>
                        </button>
                        {inc.evidencia && (
                          <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer" title="Ver evidencia">
                            <i className="ri-attachment-line text-[#6B7280]"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Nueva Incidencia */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Nueva Incidencia</h2>
              <button onClick={() => setShowNewModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Operación Asociada *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Seleccionar operación...</option>
                    <option>OP-2024-1823 - Importadora Global S.A.</option>
                    <option>OP-2024-1815 - Exportadora del Sur Ltda.</option>
                    <option>OP-2024-1808 - Comercial Andina S.A.C.</option>
                    <option>OP-2024-1801 - Distribuidora Pacífico E.I.R.L.</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Incidencia *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Seleccionar tipo...</option>
                    <option>Documentación</option>
                    <option>Transporte</option>
                    <option>Aduana</option>
                    <option>Cliente</option>
                    <option>Almacenaje</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción del Problema *</label>
                <textarea 
                  rows={4} 
                  placeholder="Describe detalladamente la incidencia..." 
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"
                  maxLength={500}
                ></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gravedad *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="critica">Crítica</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Responsable *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Seleccionar responsable...</option>
                    <option>María González</option>
                    <option>Carlos Ramírez</option>
                    <option>Ana Torres</option>
                    <option>Luis Mendoza</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Evidencia (opcional)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#2F80ED] transition-colors cursor-pointer">
                  <i className="ri-upload-cloud-line text-3xl text-[#6B7280] mb-2"></i>
                  <p className="text-sm text-[#6B7280]">Arrastra archivos o haz clic para seleccionar</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG hasta 10MB</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowNewModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button 
                onClick={() => {
                  setShowNewModal(false);
                  displayToast('Incidencia INC-2024-0090 creada exitosamente');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
              >
                Crear Incidencia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle Incidencia */}
      {showDetailModal && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg bg-[#0A2A43]/10 flex items-center justify-center`}>
                  <i className={`${getTipoIcon(selectedIncident.tipo)} text-[#0A2A43] text-2xl`}></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0A2A43]">{selectedIncident.correlativo}</h2>
                  <p className="text-sm text-[#6B7280]">{selectedIncident.operacion} - {selectedIncident.cliente}</p>
                </div>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getGravedadColor(selectedIncident.gravedad)}`}>
                  {selectedIncident.gravedad.toUpperCase()}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoColor(selectedIncident.estado)}`}>
                  {getEstadoLabel(selectedIncident.estado)}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#6B7280]">
                  <i className={`${getTipoIcon(selectedIncident.tipo)} mr-1`}></i>
                  {selectedIncident.tipo}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-[#0A2A43] mb-2">Descripción</h3>
                <p className="text-sm text-[#6B7280]">{selectedIncident.descripcion}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Responsable</label>
                  <p className="text-sm font-medium text-[#0A2A43]">{selectedIncident.responsable}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Fecha Creación</label>
                  <p className="text-sm font-medium text-[#0A2A43]">{selectedIncident.fechaCreacion}</p>
                </div>
                {selectedIncident.fechaResolucion && (
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Fecha Resolución</label>
                    <p className="text-sm font-medium text-[#10B981]">{selectedIncident.fechaResolucion}</p>
                  </div>
                )}
                {selectedIncident.evidencia && (
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Evidencia</label>
                    <button className="text-sm font-medium text-[#2F80ED] hover:underline cursor-pointer flex items-center gap-1">
                      <i className="ri-attachment-line"></i>
                      {selectedIncident.evidencia}
                    </button>
                  </div>
                )}
              </div>

              {selectedIncident.estado !== 'resuelta' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Actualizar Estado</label>
                    <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                      <option value="abierta">Abierta</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="resuelta">Resuelta</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Comentario de Resolución</label>
                    <textarea 
                      rows={3} 
                      placeholder="Describe las acciones tomadas..." 
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"
                      maxLength={500}
                    ></textarea>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cerrar
              </button>
              {selectedIncident.estado !== 'resuelta' && (
                <button 
                  onClick={() => {
                    setShowDetailModal(false);
                    displayToast('Incidencia actualizada correctamente');
                  }}
                  className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
                >
                  Guardar Cambios
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-[#10B981] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-pulse">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="font-medium">{toastMessage}</span>
        </div>
      )}
    </Layout>
  );
}
